// Piezas comunes de la interfaz. Un solo vocabulario: mismo botón, mismo campo, mismo chip en todas las pantallas.
import { useEffect, useId, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { LoaderCircle, RefreshCw, X } from 'lucide-react';
import { ErrorDeApi, mensajeDe } from '../api/cliente.ts';
import { detalleLegible } from '../logica/ediciones.ts';

type Variante = 'principal' | 'secundario' | 'fantasma' | 'peligro' | 'noche';

export function Boton({
  variante = 'secundario',
  chico,
  cargando,
  icono,
  iconoDespues,
  children,
  className,
  disabled,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; chico?: boolean; cargando?: boolean; icono?: ReactNode; iconoDespues?: boolean }) {
  return (
    <button
      type="button"
      className={['boton', `boton--${variante}`, chico ? 'boton--chico' : '', className ?? ''].join(' ')}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      {...resto}
    >
      {cargando ? <LoaderCircle className="boton__giro" aria-hidden /> : iconoDespues ? null : icono}
      {children !== undefined && <span>{children}</span>}
      {!cargando && iconoDespues ? icono : null}
    </button>
  );
}

interface BaseCampo {
  etiqueta: ReactNode;
  ayuda?: ReactNode;
  error?: string | null;
  className?: string;
}

function Envoltura({ id, etiqueta, ayuda, error, className, children }: BaseCampo & { id: string; children: ReactNode }) {
  return (
    <div className={['campo', error ? 'campo--error' : '', className ?? ''].join(' ')}>
      <label className="campo__etiqueta" htmlFor={id}>
        {etiqueta}
      </label>
      {children}
      {error ? (
        <p className="campo__error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : ayuda ? (
        <p className="campo__ayuda" id={`${id}-ayuda`}>
          {ayuda}
        </p>
      ) : null}
    </div>
  );
}

export function CampoTexto({ etiqueta, ayuda, error, className, sufijo, ...resto }: BaseCampo & InputHTMLAttributes<HTMLInputElement> & { sufijo?: ReactNode }) {
  const id = useId();
  return (
    <Envoltura id={id} etiqueta={etiqueta} ayuda={ayuda} error={error} className={className}>
      <div className="campo__caja">
        <input
          id={id}
          className="campo__control"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : ayuda ? `${id}-ayuda` : undefined}
          {...resto}
        />
        {sufijo && <span className="campo__sufijo">{sufijo}</span>}
      </div>
    </Envoltura>
  );
}

export function CampoArea({ etiqueta, ayuda, error, className, ...resto }: BaseCampo & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <Envoltura id={id} etiqueta={etiqueta} ayuda={ayuda} error={error} className={className}>
      <textarea
        id={id}
        className="campo__control campo__control--area"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : ayuda ? `${id}-ayuda` : undefined}
        {...resto}
      />
    </Envoltura>
  );
}

export function CampoSelect({
  etiqueta,
  ayuda,
  error,
  className,
  opciones,
  ...resto
}: BaseCampo & SelectHTMLAttributes<HTMLSelectElement> & { opciones: readonly { valor: string; texto: string }[] }) {
  const id = useId();
  return (
    <Envoltura id={id} etiqueta={etiqueta} ayuda={ayuda} error={error} className={className}>
      <div className="campo__caja campo__caja--select">
        <select id={id} className="campo__control" aria-invalid={error ? true : undefined} {...resto}>
          {opciones.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.texto}
            </option>
          ))}
        </select>
      </div>
    </Envoltura>
  );
}

/** Pregunta de opción única con botones grandes (mejor que un select en el celular). */
export function Segmentado<T extends string>({
  etiqueta,
  valor,
  opciones,
  alCambiar,
  ayuda,
  error,
  columnas,
  grande,
}: {
  etiqueta: ReactNode;
  valor: T | null;
  /** deshabilitada: se enseña pero no se puede elegir (p. ej. «Próximamente»). */
  opciones: readonly { valor: T; texto: ReactNode; detalle?: ReactNode; icono?: ReactNode; deshabilitada?: boolean }[];
  alCambiar: (v: T) => void;
  ayuda?: ReactNode;
  error?: string | null;
  /** 1 = una debajo de otra (opciones con detalle largo en el celular). */
  columnas?: 1 | 2 | 3 | 4;
  /** grande = tarjetas altas (el primer paso de «Nueva cotización»). */
  grande?: boolean;
}) {
  const id = useId();
  return (
    <fieldset className={['segmentado', grande ? 'segmentado--grande' : '', error ? 'campo--error' : ''].join(' ')} aria-describedby={ayuda ? `${id}-ayuda` : undefined}>
      <legend className="campo__etiqueta">{etiqueta}</legend>
      <div className={`segmentado__opciones segmentado__opciones--${columnas ?? opciones.length}`}>
        {opciones.map((o) => (
          <label key={o.valor} className={['segmentado__opcion', valor === o.valor ? 'es-activo' : '', o.detalle ? 'con-detalle' : '', o.deshabilitada ? 'es-inactivo' : ''].join(' ')}>
            <input type="radio" name={id} value={o.valor} checked={valor === o.valor} disabled={o.deshabilitada} onChange={() => alCambiar(o.valor)} />
            {o.icono && <span className="segmentado__icono">{o.icono}</span>}
            <span className="segmentado__texto">
              <span className="segmentado__titulo">{o.texto}</span>
              {o.detalle && <span className="segmentado__detalle">{o.detalle}</span>}
            </span>
          </label>
        ))}
      </div>
      {error ? (
        <p className="campo__error" role="alert">
          {error}
        </p>
      ) : ayuda ? (
        <p className="campo__ayuda" id={`${id}-ayuda`}>
          {ayuda}
        </p>
      ) : null}
    </fieldset>
  );
}

export function Interruptor({ etiqueta, detalle, marcado, alCambiar, disabled }: { etiqueta: ReactNode; detalle?: ReactNode; marcado: boolean; alCambiar: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={['interruptor', disabled ? 'es-inactivo' : ''].join(' ')}>
      <span className="interruptor__texto">
        <span className="interruptor__titulo">{etiqueta}</span>
        {detalle && <span className="interruptor__detalle">{detalle}</span>}
      </span>
      <input type="checkbox" role="switch" checked={marcado} disabled={disabled} onChange={(e) => alCambiar(e.target.checked)} />
      <span className="interruptor__pista" aria-hidden>
        <span className="interruptor__bola" />
      </span>
    </label>
  );
}

export function Chip({ tono = 'neutro', children, icono }: { tono?: 'neutro' | 'rojo' | 'ambar' | 'verde' | 'prusia' | 'noche'; children: ReactNode; icono?: ReactNode }) {
  return (
    <span className={`chip chip--${tono}`}>
      {icono}
      {children}
    </span>
  );
}

export function Cargando({ renglones = 3, etiqueta = 'Cargando…' }: { renglones?: number; etiqueta?: string }) {
  return (
    <div className="cargando" role="status" aria-label={etiqueta}>
      {Array.from({ length: renglones }, (_, i) => (
        <span key={i} className="cargando__renglon" style={{ width: `${92 - i * 14}%` }} />
      ))}
    </div>
  );
}

export function Vacio({ titulo, children, accion }: { titulo: string; children?: ReactNode; accion?: ReactNode }) {
  return (
    <div className="vacio">
      <p className="vacio__titulo">{titulo}</p>
      {children && <div className="vacio__texto">{children}</div>}
      {accion && <div className="vacio__accion">{accion}</div>}
    </div>
  );
}

/** Error de una acción, en rojo, con sus detalles. Si fue un conflicto (otra persona guardó), ofrece recargar. */
export function ErrorEnLinea({ error, alRecargar, alCerrar }: { error: unknown; alRecargar?: () => void; alCerrar?: () => void }) {
  if (!error) return null;
  const e = error instanceof ErrorDeApi ? error : null;
  return (
    <div className="error-linea" role="alert">
      <div className="error-linea__cuerpo">
        <p className="error-linea__mensaje">{mensajeDe(error)}</p>
        {/* Los detalles de un CONFLICTO son para diagnosticar («revisión que tenía la pantalla: 3; guardada: 4»): el mensaje
            ya dice quién guardó qué y cuándo. */}
        {e && e.codigo !== 'CONFLICTO' && e.detalles.length > 0 && (
          <ul className="error-linea__detalles">
            {e.detalles.slice(0, 12).map((d, i) => (
              // una ruta de la ampliación («/piezas/costo_viaticos_sin_iva») con su nombre («viáticos»)
              <li key={i}>{detalleLegible(d)}</li>
            ))}
          </ul>
        )}
        {e?.esConflicto && alRecargar && (
          <Boton chico variante="secundario" icono={<RefreshCw aria-hidden />} onClick={alRecargar}>
            Recargar la cotización
          </Boton>
        )}
      </div>
      {alCerrar && (
        <button type="button" className="icono-boton" aria-label="Cerrar el error" onClick={alCerrar}>
          <X aria-hidden />
        </button>
      )}
    </div>
  );
}

/** Diálogo nativo (<dialog>): foco atrapado y Esc gratis. Solo para lo que de verdad interrumpe. */
export function Dialogo({ abierto, alCerrar, titulo, children, pie, ancho }: { abierto: boolean; alCerrar: () => void; titulo: string; children: ReactNode; pie?: ReactNode; ancho?: 'normal' | 'grande' }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    }
    if (!abierto && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [abierto]);
  return (
    <dialog
      ref={ref}
      className={['dialogo', ancho === 'grande' ? 'dialogo--grande' : ''].join(' ')}
      onClose={alCerrar}
      onCancel={(e) => {
        e.preventDefault();
        alCerrar();
      }}
      aria-label={titulo}
    >
      {abierto && (
        <div className="dialogo__hoja">
          <header className="dialogo__cabeza">
            <h2 className="dialogo__titulo">{titulo}</h2>
            <button type="button" className="icono-boton" aria-label="Cerrar" onClick={alCerrar}>
              <X aria-hidden />
            </button>
          </header>
          <div className="dialogo__cuerpo">{children}</div>
          {pie && <footer className="dialogo__pie">{pie}</footer>}
        </div>
      )}
    </dialog>
  );
}

export function Seccion({ titulo, descripcion, acciones, children, id }: { titulo: ReactNode; descripcion?: ReactNode; acciones?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className="seccion" id={id} aria-labelledby={id ? `${id}-titulo` : undefined}>
      <header className="seccion__cabeza">
        <div>
          <h2 className="seccion__titulo" id={id ? `${id}-titulo` : undefined}>
            {titulo}
          </h2>
          {descripcion && <p className="seccion__descripcion">{descripcion}</p>}
        </div>
        {acciones && <div className="seccion__acciones">{acciones}</div>}
      </header>
      {children}
    </section>
  );
}

/** Botón de acción destructiva en dos toques: el primero pregunta ("¿Quitar?"), el segundo hace. Sin ventana emergente. */
export function BotonConfirmar({
  children,
  pregunta,
  alConfirmar,
  cargando,
  icono,
  etiquetaAccesible,
}: {
  children?: ReactNode;
  pregunta: string;
  alConfirmar: () => void;
  cargando?: boolean;
  icono?: ReactNode;
  etiquetaAccesible?: string;
}) {
  const [preguntando, setPreguntando] = useState(false);
  useEffect(() => {
    if (!preguntando) return;
    const t = window.setTimeout(() => setPreguntando(false), 5000);
    return () => window.clearTimeout(t);
  }, [preguntando]);
  if (preguntando)
    return (
      <span className="confirmar" role="group" aria-label={pregunta}>
        <span className="confirmar__pregunta">{pregunta}</span>
        <Boton chico variante="peligro" cargando={cargando} onClick={() => { setPreguntando(false); alConfirmar(); }}>
          Sí
        </Boton>
        <Boton chico variante="fantasma" onClick={() => setPreguntando(false)}>
          No
        </Boton>
      </span>
    );
  return (
    <Boton chico variante="fantasma" cargando={cargando} icono={icono} aria-label={etiquetaAccesible} onClick={() => setPreguntando(true)}>
      {children}
    </Boton>
  );
}
